import React from 'react'
import styled, { css } from 'styled-components'

import { theme } from '../../../theme'

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  children: React.ReactNode
  variant?: 'primary' | 'secondary' | 'ghost'
  size?: 'sm' | 'md' | 'lg'
  icon?: React.ReactNode
  loading?: boolean
  fullWidth?: boolean
}

const StyledButton = styled.button<{
  variant: 'primary' | 'secondary' | 'ghost'
  size: 'sm' | 'md' | 'lg'
  fullWidth: boolean
  disabled: boolean
}>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-family: ${theme.fonts.mainFont};
  font-weight: 500;
  border-radius: ${theme.radius.default};
  transition: all 0.2s ease-in-out;
  border: none;
  cursor: pointer;
  outline: none;

  &:focus {
    box-shadow: 0 0 0 2px ${theme.colors.primaryDefault}40;
  }

  ${({ variant }) => {
    switch (variant) {
      case 'primary':
        return css`
          background-color: ${theme.colors.altovita};
          color: ${theme.colors.secondaryDefault};

          &:hover:not(:disabled) {
            background-color: ${theme.colors.primaryHover};
          }

          &:focus {
            box-shadow: 0 0 0 2px ${theme.colors.altovita}40;
          }
        `
      case 'secondary':
        return css`
          background-color: #4b5563;
          color: ${theme.colors.secondaryDefault};

          &:hover:not(:disabled) {
            background-color: #374151;
          }

          &:focus {
            box-shadow: 0 0 0 2px #4b556340;
          }
        `
      case 'ghost':
        return css`
          background-color: transparent;
          border: 1px solid ${theme.colors.borderColor};
          color: ${theme.colors.altovita};

          &:hover:not(:disabled) {
            background-color: ${theme.colors.lightBg};
          }

          &:focus {
            box-shadow: 0 0 0 2px ${theme.colors.altovita}40;
          }
        `
      default:
        return ''
    }
  }}

  ${({ size }) => {
    switch (size) {
      case 'sm':
        return css`
          padding: ${theme.spacing.sm} ${theme.spacing.md};
          font-size: ${theme.fontSize.sm};
          height: 2.5rem;
        `
      case 'md':
        return css`
          padding: ${theme.spacing.md} ${theme.spacing.lg};
          font-size: ${theme.fontSize.base};
          height: 3.5rem;
        `
      case 'lg':
        return css`
          padding: ${theme.spacing.lg} ${theme.spacing.xl};
          font-size: ${theme.fontSize.lg};
          height: 4rem;
        `
      default:
        return ''
    }
  }}
  
  ${({ fullWidth }) =>
    fullWidth &&
    css`
      width: 100%;
    `}
  
  ${({ disabled }) =>
    disabled &&
    css`
      opacity: 0.5;
      cursor: not-allowed;

      &:hover {
        transform: none;
      }
    `}
  
  &:not(:disabled):hover {
    transform: translateY(-1px);
  }
`

const IconWrapper = styled.span`
  margin-right: ${theme.spacing.sm};
`

const LoadingSpinner = styled.svg`
  animation: spin 1s linear infinite;
  margin-right: ${theme.spacing.md};
  height: 1.25rem;
  width: 1.25rem;
  color: currentColor;

  @keyframes spin {
    from {
      transform: rotate(0deg);
    }
    to {
      transform: rotate(360deg);
    }
  }
`

export const Button: React.FC<ButtonProps> = ({
  children,
  variant = 'primary',
  size = 'md',
  icon,
  loading = false,
  fullWidth = false,
  className = '',
  disabled,
  ...props
}) => {
  const isDisabled = disabled || loading

  return (
    <StyledButton
      variant={variant}
      size={size}
      fullWidth={fullWidth}
      disabled={isDisabled}
      className={className}
      {...props}
    >
      {loading && (
        <LoadingSpinner
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
        >
          <circle
            className="opacity-25"
            cx="12"
            cy="12"
            r="10"
            stroke="currentColor"
            strokeWidth="4"
          />
          <path
            className="opacity-75"
            fill="currentColor"
            // eslint-disable-next-line max-len
            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
          />
        </LoadingSpinner>
      )}
      {!loading && icon && <IconWrapper>{icon}</IconWrapper>}
      {children}
    </StyledButton>
  )
}
